import { FormEvent, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import barcodeRegistrationGuideImage from "../../assets/book-barcode-registration-guide.png";
import { AdminBook, BookAdminApi } from "../../interface/book-admin-api";
import BookBarcodeScannerDialog from "../components/BookBarcodeScannerDialog";

const bookAdminApi = new BookAdminApi();

function isBook(value: unknown): value is AdminBook {
  const book = value as Partial<AdminBook> | undefined;
  return Boolean(book && typeof book.sk === "string" && typeof book.title === "string");
}

function getCatalogErrorMessage(response: { response?: { error_msg?: unknown } } | undefined) {
  if (response?.response?.error_msg === "Missing Authentication Token") {
    return "도서 관리 API가 아직 운영 게이트웨이에 배포되지 않았습니다. 배포 후 다시 시도해 주세요.";
  }
  return "도서 검색에 실패했습니다. 잠시 후 다시 시도해 주세요.";
}

function getRegistrationErrorMessage(errorMessage: unknown) {
  if (errorMessage === "barcodeAlreadyExists") return "이미 다른 도서에 등록된 바코드입니다.";
  if (errorMessage === "versionConflict") return "도서 정보가 변경되었습니다. 다시 검색한 뒤 등록해 주세요.";
  return "바코드를 등록하지 못했습니다. 잠시 후 다시 시도해 주세요.";
}

function getRegistrationMessage(book: AdminBook, barcode: string) {
  if (!book.barcode) return `${book.title}에 ${barcode} 바코드를 등록하시겠습니까?`;
  if (book.barcode === barcode) return `${book.title}의 ${barcode} 바코드를 다시 등록하시겠습니까?`;
  return `${book.title}의 기존 바코드 ${book.barcode}를 ${barcode}로 변경하시겠습니까?`;
}

const BookBarcodeAdmin = () => {
  const [title, setTitle] = useState("");
  const [books, setBooks] = useState<AdminBook[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedBook, setSelectedBook] = useState<AdminBook | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [pendingBarcode, setPendingBarcode] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ severity: "success" | "error"; message: string } | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);

  const orderedBooks = useMemo(
    () => [...books].sort((left, right) => left.title.localeCompare(right.title, "ko")),
    [books],
  );

  const loadBooks = async (searchTitle: string, preserveStatus = false) => {
    setLoading(true);
    setHasSearched(true);
    if (!preserveStatus) setStatus(null);
    const response = await bookAdminApi.get_book_admin_catalog({ title: searchTitle });
    const catalog = response?.response?.result;
    const result = Array.isArray(catalog) ? catalog : catalog?.books;
    if (response?.code !== "200" || !Array.isArray(result)) {
      setBooks([]);
      setStatus({ severity: "error", message: getCatalogErrorMessage(response) });
      setLoading(false);
      return;
    }
    setBooks(result.filter(isBook));
    setLoading(false);
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const searchTitle = title.trim();
    if (!searchTitle) {
      setStatus({ severity: "error", message: "도서 제목을 입력해 주세요." });
      return;
    }
    void loadBooks(searchTitle);
  };

  const openScanner = (book: AdminBook) => {
    setSelectedBook(book);
    setPendingBarcode("");
    setStatus(null);
    setScannerOpen(true);
  };

  const handleScan = (barcode: string) => {
    setPendingBarcode(barcode);
    setScannerOpen(false);
  };

  const closeRegistration = () => {
    if (saving) return;
    setPendingBarcode("");
    setSelectedBook(null);
  };

  const registerBarcode = async () => {
    if (!selectedBook || !pendingBarcode) return;
    setSaving(true);
    setStatus(null);
    const response = await bookAdminApi.register_book_barcode({
      sk: selectedBook.sk,
      version: selectedBook.version,
      barcode: pendingBarcode,
    });
    if (response?.code !== "200") {
      setStatus({ severity: "error", message: getRegistrationErrorMessage(response?.response?.error_msg) });
      setSaving(false);
      setPendingBarcode("");
      setSelectedBook(null);
      return;
    }

    const searchTitle = title.trim();
    setSaving(false);
    setPendingBarcode("");
    setSelectedBook(null);
    setStatus({ severity: "success", message: "바코드가 등록되었습니다." });
    await loadBooks(searchTitle, true);
  };

  return (
    <main className="hpoint-renewal hp-barcode-registration">
      <header className="hp-barcode-registration__header">
        <div className="hp-barcode-registration__header-top">
          <span>SMALL LIBRARY</span>
          <button className="hp-barcode-registration__guide-trigger" type="button" onClick={() => setGuideOpen(true)}>사용법</button>
        </div>
        <h1>바코드 등록</h1>
        <p>도서를 검색한 뒤 바코드를 스캔해 등록하세요.</p>
      </header>

      <form className="hp-barcode-registration__search" role="search" onSubmit={handleSearch} noValidate>
        <label htmlFor="book-barcode-title">도서 제목 검색</label>
        <div>
          <input
            id="book-barcode-title"
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              if (status?.severity === "error") setStatus(null);
            }}
            placeholder="도서명을 입력해 주세요"
            autoFocus
          />
          <button type="submit" aria-label="검색" disabled={loading}>
            <SearchRoundedIcon aria-hidden="true" />
          </button>
        </div>
      </form>

      {status ? <Alert className="hp-barcode-registration__alert" severity={status.severity}>{status.message}</Alert> : null}

      <section className="hp-barcode-registration__results" aria-label="바코드 등록 도서 검색 결과">
        <div className="hp-barcode-registration__results-heading">
          <h2>도서 검색</h2>
          {hasSearched && !loading ? <span>{orderedBooks.length}권</span> : null}
        </div>
        {loading ? <p className="hp-barcode-registration__message">도서를 검색하는 중입니다.</p> : null}
        {!loading && !hasSearched ? <p className="hp-barcode-registration__message">도서명을 입력하고 검색해 주세요.</p> : null}
        {!loading && hasSearched && !orderedBooks.length ? <p className="hp-barcode-registration__message">검색 결과가 없습니다.</p> : null}
        {!loading && orderedBooks.length ? (
          <div className="hp-barcode-registration__list" role="list">
            {orderedBooks.map((book) => (
              <article className="hp-barcode-registration__item" key={book.sk} role="listitem">
                <div className="hp-barcode-registration__item-copy">
                  <div className="hp-barcode-registration__item-title">
                    <h3>{book.title}</h3>
                    <Chip
                      className={`hp-barcode-registration__badge${book.barcode ? " hp-barcode-registration__badge--registered" : " hp-barcode-registration__badge--unregistered"}`}
                      label={book.barcode ? "등록" : "미등록"}
                      color={book.barcode ? "error" : "info"}
                      size="small"
                    />
                  </div>
                  <p className="hp-barcode-registration__barcode">
                    <span>현재 바코드</span>
                    {book.barcode ? <strong className="hp-barcode-registration__barcode-value">{book.barcode}</strong> : "등록된 바코드 없음"}
                  </p>
                </div>
                <button
                  className="hp-barcode-registration__scan-button"
                  type="button"
                  aria-label={`${book.title} 바코드 등록`}
                  onClick={() => openScanner(book)}
                >
                  <QrCodeScannerRoundedIcon aria-hidden="true" />
                </button>
              </article>
            ))}
          </div>
        ) : null}
      </section>

      {scannerOpen ? (
        <BookBarcodeScannerDialog
          onScan={handleScan}
          onClose={() => setScannerOpen(false)}
          manualFallback
        />
      ) : null}

      <Dialog
        className="hp-dialog hp-barcode-registration__guide-dialog"
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: "hp-dialog__paper" }}
      >
        <DialogTitle>바코드 등록 사용법</DialogTitle>
        <DialogContent dividers className="hp-barcode-registration__guide-content">
          <p>책 뒷면 하단의 바코드를 화면 중앙에 맞춰 스캔해 주세요.</p>
          <img src={barcodeRegistrationGuideImage} alt="책 뒷면 바코드 위치 예시" />
        </DialogContent>
        <DialogActions>
          <Button className="hp-button hp-button--primary" onClick={() => setGuideOpen(false)} variant="contained">닫기</Button>
        </DialogActions>
      </Dialog>

      <Dialog className="hp-dialog" open={Boolean(selectedBook && pendingBarcode)} onClose={closeRegistration} fullWidth maxWidth="xs" PaperProps={{ className: "hp-dialog__paper" }}>
        <DialogTitle>바코드 등록 확인</DialogTitle>
        <DialogContent dividers>
          {selectedBook ? <p>{getRegistrationMessage(selectedBook, pendingBarcode)}</p> : null}
        </DialogContent>
        <DialogActions>
          <Button className="hp-button hp-button--secondary" onClick={closeRegistration} disabled={saving}>취소</Button>
          <Button className="hp-button hp-button--primary" onClick={() => void registerBarcode()} variant="contained" disabled={saving}>
            {saving ? "등록 중" : "등록"}
          </Button>
        </DialogActions>
      </Dialog>
    </main>
  );
};

export default BookBarcodeAdmin;
